import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  Optional,
} from "@angular/core";
import { ModalController, Platform } from "@ionic/angular";
import { Subscription } from "rxjs";
import { Store } from "@ngrx/store";
import * as fromApp from "../../../store/app.reducer";
import * as _ from "lodash";

@Component({
  selector: "app-rating-details",
  templateUrl: "./rating-details.component.html",
  styleUrls: ["./rating-details.component.scss"],
})
export class RatingDetailsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() currentReview: any;
  public reviewStarAverageDisplay: string | null = null;
  public reviewStarAverageValue: number | null = null;
  public ratingQuestionsCount = 0;
  public averageColor: string | null = null;
  public averageStarIcons: string[] = [];
  public starQuestionDetails: Array<StarQuestionDetail | null> = [];
  public animateProgress = false; // start with empty bars

  public selectedLanguage: string | null = null;
  public mo_base: string = "en";

  private readonly starIconIndexes = [0, 1, 2, 3, 4];
  private groupAppSubscription: Subscription;
  private unsubscribeBackEvent: Subscription;

  constructor(
    private modalCtrl: ModalController,
    private platform: Platform,
    @Optional() private store?: Store<fromApp.AppState>
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes["currentReview"] && this.currentReview) {
      this.initializeRatingSummary();
    }
  }

  ngOnInit() {
    // Subscribe to language changes for translations fallback

    this.groupAppSubscription = this.store
      .select("groupApp")
      .subscribe((state) => {
        if (state) {
          if (
            state.selectedLangugage &&
            !_.isEqual(this.selectedLanguage, state.selectedLangugage)
          ) {
            this.selectedLanguage = _.cloneDeep(state.selectedLangugage);
          }

          if (state.groupApp && state.groupApp.languages) {
            const base = _.find(state.groupApp.languages, { mo_base: true });
            if (base && base.code) {
              this.mo_base = base.code;
            } else {
              this.mo_base = "en";
            }
          }
        }
      });

    this.unsubscribeBackEvent = this.platform.backButton.subscribeWithPriority(
      200,
      () => {
        if (this.platform.is("android")) {
          this.modalCtrl.dismiss();
        }
      }
    );
    console.log("current-review", this.currentReview);
    if (this.currentReview) {
      this.initializeRatingSummary();
    }
    // Trigger progress animation shortly after render
    setTimeout(() => (this.animateProgress = true), 50);
  }

  closeModal() {
    this.modalCtrl.dismiss();
  }

  calculateDetails(stars) {
    let starColor;

    if (stars !== 0) {
      stars = stars.toFixed(2);

      if (stars && parseFloat(stars.toString())) {
        if (stars <= 2.5) {
          starColor = "danger";
        } else if (stars > 2.5 && stars <= 3.5) {
          starColor = "warning";
        } else if (stars > 3.5) {
          starColor = "complete";
        }
      }
    }

    return starColor;
  }

  private initializeRatingSummary() {
    this.reviewStarAverageDisplay = null;
    this.reviewStarAverageValue = null;
    this.ratingQuestionsCount = 0;
    this.averageColor = null;
    this.averageStarIcons = this.starIconIndexes.map(() => "star-outline");

    if (
      !this.currentReview ||
      !this.currentReview.questions ||
      !this.currentReview.questions.length
    ) {
      this.starQuestionDetails = [];
      return;
    }

    this.starQuestionDetails = this.currentReview.questions.map((question) => {
      if (question && question.type === "stars") {
        return this.buildStarQuestionDetail(question.stars);
      }
      return null;
    });

    let sum = 0;
    let count = 0;

    this.starQuestionDetails.forEach((detail) => {
      if (detail && detail.numericValue !== null) {
        sum += detail.numericValue;
        count = count + 1;
      }
    });

    this.ratingQuestionsCount = count;

    if (count > 0) {
      const average = sum / count;
      this.reviewStarAverageValue = average;
      this.reviewStarAverageDisplay = average.toFixed(1);
      this.averageColor = this.calculateDetails(average);
      this.averageStarIcons = this.starIconIndexes.map((index) =>
        this.resolveStarIcon(index, average)
      );
    }
  }

  private buildStarQuestionDetail(stars: any): StarQuestionDetail {
    const hasValue = stars === 0 || stars === "0" || !!stars;

    if (!hasValue) {
      return {
        formattedValue: "-",
        progress: 0,
        color: null,
        numericValue: null,
      };
    }

    const numeric = Number(stars);

    if (isNaN(numeric)) {
      return {
        formattedValue: "-",
        progress: 0,
        color: null,
        numericValue: null,
      };
    }

    const clamped = Math.max(0, Math.min(numeric, 5));

    return {
      formattedValue: clamped.toFixed(1),
      progress: clamped / 5,
      color: this.calculateDetails(clamped),
      numericValue: clamped,
    };
  }

  private resolveStarIcon(index: number, average: number) {
    const threshold = index + 1;

    if (average >= threshold) {
      return "star";
    }

    if (average >= threshold - 0.5) {
      return "star-half";
    }

    return "star-outline";
  }

  ngOnDestroy() {
    if (this.unsubscribeBackEvent) {
      this.unsubscribeBackEvent.unsubscribe();
    }

    if (this.groupAppSubscription) {
      this.groupAppSubscription.unsubscribe();
    }
  }
}

interface StarQuestionDetail {
  formattedValue: string;
  progress: number;
  color: string | null;
  numericValue: number | null;
}
